/* eslint-disable react/no-unknown-property */
import { useMemo, useEffect, useCallback, useRef } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { shaderMaterial } from '@react-three/drei';
import * as THREE from 'three';

const GooeyFilter = ({ id = 'goo-filter', strength = 10 }) => {
  return (
    <svg className="absolute overflow-hidden z-1">
      <defs>
        <filter id={id}>
          <feGaussianBlur in="SourceGraphic" stdDeviation={strength} result="blur" />
          <feColorMatrix in="blur" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9" result="goo" />
          <feComposite in="SourceGraphic" in2="goo" operator="atop" />
        </filter>
      </defs>
    </svg>
  );
};

const DotMaterial = shaderMaterial(
  {
    resolution: new THREE.Vector2(),
    mouseTrail: null,
    gridSize: 100,
    pixelColor: new THREE.Color('#ffffff')
  },
  `
    varying vec2 vUv;
    void main() {
      gl_Position = vec4(position.xy, 0.0, 1.0);
    }
  `,
  `
    uniform vec2 resolution;
    uniform sampler2D mouseTrail;
    uniform float gridSize;
    uniform vec3 pixelColor;

    vec2 coverUv(vec2 uv) {
      vec2 s = resolution.xy / max(resolution.x, resolution.y);
      vec2 newUv = (uv - 0.5) * s + 0.5;
      return clamp(newUv, 0.0, 1.0);
    }

    void main() {
      vec2 screenUv = gl_FragCoord.xy / resolution;
      vec2 uv = coverUv(screenUv);

      vec2 gridUv = fract(uv * gridSize);
      vec2 gridUvCenter = (floor(uv * gridSize) + 0.5) / gridSize;

      float trail = texture2D(mouseTrail, gridUvCenter).r;

      gl_FragColor = vec4(pixelColor, trail);
    }
  `
);

// Trail texture manager - creates and manages a canvas-based trail texture
function useManualTrailTexture({ size = 512, radius = 0.1, maxAge = 250 }) {
  const canvasRef = useRef(null);
  const textureRef = useRef(null);
  const trailRef = useRef([]);

  if (!canvasRef.current) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    canvasRef.current = canvas;
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.NearestFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    textureRef.current = texture;
  }

  const addPoint = useCallback((x, y) => {
    // x, y should be 0-1 normalized
    trailRef.current.push({ x, y, age: 0 });
  }, []);

  const update = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, size, size);

    const trail = trailRef.current;
    for (let i = trail.length - 1; i >= 0; i--) {
      const p = trail[i];
      p.age++;
      if (p.age > maxAge) {
        trail.splice(i, 1);
        continue;
      }
      const alpha = 1 - p.age / maxAge;
      const r = radius * size;
      ctx.beginPath();
      const gradient = ctx.createRadialGradient(
        p.x * size, p.y * size, 0,
        p.x * size, p.y * size, r
      );
      gradient.addColorStop(0, `rgba(255,255,255,${alpha})`);
      gradient.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = gradient;
      ctx.arc(p.x * size, p.y * size, r, 0, Math.PI * 2);
      ctx.fill();
    }

    textureRef.current.needsUpdate = true;
  }, [size, radius, maxAge]);

  return { texture: textureRef.current, addPoint, update };
}

function Scene({ gridSize, trailSize, maxAge, interpolate, pixelColor, mouseRef }) {
  const size = useThree(s => s.size);
  const viewport = useThree(s => s.viewport);
  const gl = useThree(s => s.gl);

  const dotMaterial = useMemo(() => new DotMaterial(), []);
  dotMaterial.uniforms.pixelColor.value = new THREE.Color(pixelColor);

  const { texture, addPoint, update } = useManualTrailTexture({
    size: 512,
    radius: trailSize,
    maxAge: maxAge
  });

  const lastPos = useRef({ x: -1, y: -1 });

  useFrame(() => {
    const mouse = mouseRef.current;
    if (mouse.x >= 0 && mouse.y >= 0) {
      const canvas = gl.domElement;
      const rect = canvas.getBoundingClientRect();
      // Normalize mouse position to 0-1 relative to canvas
      const nx = (mouse.x - rect.left) / rect.width;
      const ny = (mouse.y - rect.top) / rect.height;

      if (nx >= 0 && nx <= 1 && ny >= 0 && ny <= 1) {
        const dx = nx - lastPos.current.x;
        const dy = ny - lastPos.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (lastPos.current.x < 0 || dist > 0.002) {
          // Interpolate points along the path for smooth trails
          const steps = Math.max(1, Math.floor(dist * (interpolate || 5) * 50));
          for (let i = 0; i < steps; i++) {
            const t = i / steps;
            const px = lastPos.current.x < 0 ? nx : lastPos.current.x + dx * t;
            const py = lastPos.current.y < 0 ? ny : lastPos.current.y + dy * t;
            addPoint(px, py);
          }
          lastPos.current = { x: nx, y: ny };
        }
      }
    }

    update();
  });

  const scale = Math.max(viewport.width, viewport.height) / 2;

  return (
    <mesh scale={[scale, scale, 1]}>
      <planeGeometry args={[2, 2]} />
      <primitive
        object={dotMaterial}
        gridSize={gridSize}
        resolution={[size.width * viewport.dpr, size.height * viewport.dpr]}
        mouseTrail={texture}
        transparent={true}
      />
    </mesh>
  );
}

export default function PixelTrail({
  gridSize = 40,
  trailSize = 0.1,
  maxAge = 250,
  interpolate = 5,
  easingFunction = x => x,
  canvasProps = {},
  glProps = {
    antialias: false,
    powerPreference: 'high-performance',
    alpha: true
  },
  gooeyFilter,
  color = '#ffffff',
  className = ''
}) {
  const mouseRef = useRef({ x: -1, y: -1 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointermove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('pointermove', handleMouseMove);
  }, []);

  return (
    <>
      {gooeyFilter && <GooeyFilter id={gooeyFilter.id} strength={gooeyFilter.strength} />}
      <Canvas
        {...canvasProps}
        gl={glProps}
        className={`absolute z-1 ${className}`}
        style={{
          pointerEvents: 'none',
          ...(gooeyFilter && { filter: `url(#${gooeyFilter.id})` })
        }}
      >
        <Scene
          gridSize={gridSize}
          trailSize={trailSize}
          maxAge={maxAge}
          interpolate={interpolate}
          easingFunction={easingFunction}
          pixelColor={color}
          mouseRef={mouseRef}
        />
      </Canvas>
    </>
  );
}

